import React, { memo } from 'react';
import { ArrowRightIcon, ShuffleIcon, TrendingUpIcon, CheckSquareIcon, HeartIcon, LockIcon, SmileIcon, HomeIcon } from 'lucide-react';
const Consolidation = () => {
  const pillars = [{
    id: 1,
    title: 'Imprevisibilidad Controlada',
    icon: <ShuffleIcon size={24} className="text-purple-500" data-id="element-193" />,
    description: 'Mantén un equilibrio entre previsibilidad y sorpresa. Sé consistente en valores, pero impredecible en comportamientos.',
    tips: ['Sorprende con pequeños gestos inesperados', 'Alterna entre disponibilidad y misterio', 'Desarrolla nuevos intereses periódicamente', 'Evita rutinas excesivamente predecibles']
  }, {
    id: 2,
    title: 'Crecimiento Personal Continuo',
    icon: <TrendingUpIcon size={24} className="text-blue-500" data-id="element-194" />,
    description: 'Continúa evolucionando como persona independiente. Tu crecimiento personal mantiene su interés y admiración.',
    tips: ['Establece metas personales desafiantes', 'Aprende nuevas habilidades regularmente', 'Comparte tus éxitos sin arrogancia', 'Pide consejos en áreas donde él tiene experiencia']
  }, {
    id: 3,
    title: 'Validación Intermitente',
    icon: <CheckSquareIcon size={24} className="text-green-500" data-id="element-195" />,
    description: 'Alterna entre momentos de validación genuina y períodos de independencia emocional.',
    tips: ['Elogia logros específicos, no generalidades', 'Varía entre demostrar admiración y autonomía', 'Reconoce cualidades únicas de él periódicamente', 'Evita validación excesiva o predecible']
  }, {
    id: 4,
    title: 'Independencia Atractiva',
    icon: <LockIcon size={24} className="text-orange-500" data-id="element-196" />,
    description: 'Mantén tu individualidad, intereses propios y círculo social independiente.',
    tips: ['Cultiva amistades y hobbies propios', 'Establece límites saludables', 'Toma decisiones independientes cuando sea apropiado', 'Demuestra autosuficiencia emocional']
  }, {
    id: 5,
    title: 'Intimidad Estratégica',
    icon: <HeartIcon size={24} className="text-red-500" data-id="element-197" />,
    description: 'Crea momentos de profunda conexión emocional y física, alternados con espacio para independencia.',
    tips: ['Desarrolla rituales de conexión exclusivos', 'Practica vulnerabilidad auténtica en momentos clave', 'Respeta su espacio sin inseguridad', 'Crea memorias emocionales intensas periódicamente']
  }, {
    id: 6,
    title: 'Celos Constructivos',
    icon: <SmileIcon size={24} className="text-yellow-500" data-id="element-198" />,
    description: 'Genera celos leves y saludables que reafirmen el valor de la relación, sin manipulación o juegos destructivos.',
    tips: ['Mantente atractiva y deseable socialmente', 'Deja claro que tienes opciones, pero eliges estar con él', 'Menciona admiradores de forma casual y no amenazante', 'Nunca uses celos para lastimar o controlar']
  }, {
    id: 7,
    title: 'Futuro Compartido',
    icon: <HomeIcon size={24} className="text-teal-500" data-id="element-199" />,
    description: 'Crea una visión de futuro juntos que sea emocionante, desafiante y alineada con los valores de ambos.',
    tips: ['Discute sueños y metas de largo plazo', 'Crea proyectos conjuntos significativos', 'Visualiza experiencias futuras de forma vívida', 'Construye tradiciones exclusivas de la relación']
  }];
  return <div className="max-w-4xl mx-auto" data-id="element-200">
      <header className="mb-8 text-center" data-id="element-201">
        <h1 className="text-3xl font-bold text-[#2C2C2C] mb-2" data-id="element-202">
          Consolidación de la Obsesión
        </h1>
        <p className="text-gray-600" data-id="element-203">
          Los 7 Pilares para mantener la obsesión duradera
        </p>
      </header>
      <div className="bg-white rounded-xl shadow-md p-6 mb-8" data-id="element-204">
        <h2 className="text-xl font-semibold text-[#2C2C2C] mb-6" data-id="element-205">
          Plan de 90 Días Post-Reconquista
        </h2>
        <div className="mb-6" data-id="element-206">
          <p className="text-gray-700 mb-4" data-id="element-207">
            Después de completar el programa de 21 días y reconquistar a tu
            hombre, es crucial implementar los 7 Pilares de la Obsesión Duradera
            para garantizar que permanezca enamorado y comprometido a largo
            plazo. Este plan de 90 días consolidará los resultados obtenidos.
          </p>
          <div className="bg-gray-50 border-l-4 border-[#9C27B0] p-4 mb-4" data-id="element-208">
            <p className="text-gray-700" data-id="element-209">
              <span className="font-bold" data-id="element-210">Importante:</span> La fase de
              consolidación es tan importante como la reconquista inicial.
              Muchas mujeres cometen el error de relajarse después de conseguir
              la reconciliación, solo para ver el interés de él disminuir
              nuevamente.
            </p>
          </div>
        </div>
        <div className="space-y-6" data-id="element-211">
          {pillars.map(pillar => <div key={pillar.id} className="border rounded-lg p-5" data-id="element-212">
              <div className="flex items-center mb-3" data-id="element-213">
                <div className="p-2 rounded-full bg-gray-100 mr-3" data-id="element-214">
                  {pillar.icon}
                </div>
                <h3 className="font-bold text-lg" data-id="element-215">
                  Pilar {pillar.id}: {pillar.title}
                </h3>
              </div>
              <p className="text-gray-700 mb-4" data-id="element-216">{pillar.description}</p>
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-217">
                <h4 className="font-medium text-[#9C27B0] mb-2" data-id="element-218">
                  Consejos de Implementación:
                </h4>
                <ul className="space-y-2" data-id="element-219">
                  {pillar.tips.map((tip, index) => <li key={index} className="flex items-start" data-id="element-220">
                      <div className="bg-[#9C27B0] text-white p-1 rounded-full mr-2 mt-0.5" data-id="element-221">
                        <span className="block h-1.5 w-1.5 rounded-full bg-current" data-id="element-222"></span>
                      </div>
                      <span className="text-gray-700" data-id="element-223">{tip}</span>
                    </li>)}
                </ul>
              </div>
            </div>)}
        </div>
      </div>
      <div className="bg-white rounded-xl shadow-md p-6" data-id="element-224">
        <h2 className="text-lg font-semibold text-[#2C2C2C] mb-4" data-id="element-225">
          Señales de Éxito
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-226">
          <div className="border rounded-lg p-4" data-id="element-227">
            <h3 className="font-medium text-[#E91E63] mb-3" data-id="element-228">
              Él Está Obsesionado Cuando:
            </h3>
            <ul className="space-y-2" data-id="element-229">
              <li className="flex items-start" data-id="element-230">
                <div className="bg-green-100 text-green-700 p-1 rounded-full mr-2 mt-0.5" data-id="element-231">
                  <span className="block h-1.5 w-1.5 rounded-full bg-current" data-id="element-232"></span>
                </div>
                <span className="text-gray-700" data-id="element-233">
                  Inicia contacto frecuentemente
                </span>
              </li>
              <li className="flex items-start" data-id="element-234">
                <div className="bg-green-100 text-green-700 p-1 rounded-full mr-2 mt-0.5" data-id="element-235">
                  <span className="block h-1.5 w-1.5 rounded-full bg-current" data-id="element-236"></span>
                </div>
                <span className="text-gray-700" data-id="element-237">
                  Habla sobre planes futuros juntos
                </span>
              </li>
              <li className="flex items-start" data-id="element-238">
                <div className="bg-green-100 text-green-700 p-1 rounded-full mr-2 mt-0.5" data-id="element-239">
                  <span className="block h-1.5 w-1.5 rounded-full bg-current" data-id="element-240"></span>
                </div>
                <span className="text-gray-700" data-id="element-241">
                  Demuestra celos saludables
                </span>
              </li>
              <li className="flex items-start" data-id="element-242">
                <div className="bg-green-100 text-green-700 p-1 rounded-full mr-2 mt-0.5" data-id="element-243">
                  <span className="block h-1.5 w-1.5 rounded-full bg-current" data-id="element-244"></span>
                </div>
                <span className="text-gray-700" data-id="element-245">
                  Busca tu aprobación y validación
                </span>
              </li>
              <li className="flex items-start" data-id="element-246">
                <div className="bg-green-100 text-green-700 p-1 rounded-full mr-2 mt-0.5" data-id="element-247">
                  <span className="block h-1.5 w-1.5 rounded-full bg-current" data-id="element-248"></span>
                </div>
                <span className="text-gray-700" data-id="element-249">
                  Se asegura de presentarte socialmente
                </span>
              </li>
            </ul>
          </div>
          <div className="border rounded-lg p-4" data-id="element-250">
            <h3 className="font-medium text-orange-500 mb-3" data-id="element-251">
              Señales de Alerta:
            </h3>
            <ul className="space-y-2" data-id="element-252">
              <li className="flex items-start" data-id="element-253">
                <div className="bg-orange-100 text-orange-700 p-1 rounded-full mr-2 mt-0.5" data-id="element-254">
                  <span className="block h-1.5 w-1.5 rounded-full bg-current" data-id="element-255"></span>
                </div>
                <span className="text-gray-700" data-id="element-256">
                  Contacto inconsistente o decreciente
                </span>
              </li>
              <li className="flex items-start" data-id="element-257">
                <div className="bg-orange-100 text-orange-700 p-1 rounded-full mr-2 mt-0.5" data-id="element-258">
                  <span className="block h-1.5 w-1.5 rounded-full bg-current" data-id="element-259"></span>
                </div>
                <span className="text-gray-700" data-id="element-260">
                  Evita discusiones sobre el futuro
                </span>
              </li>
              <li className="flex items-start" data-id="element-261">
                <div className="bg-orange-100 text-orange-700 p-1 rounded-full mr-2 mt-0.5" data-id="element-262">
                  <span className="block h-1.5 w-1.5 rounded-full bg-current" data-id="element-263"></span>
                </div>
                <span className="text-gray-700" data-id="element-264">
                  Mantiene la relación en secreto
                </span>
              </li>
              <li className="flex items-start" data-id="element-265">
                <div className="bg-orange-100 text-orange-700 p-1 rounded-full mr-2 mt-0.5" data-id="element-266">
                  <span className="block h-1.5 w-1.5 rounded-full bg-current" data-id="element-267"></span>
                </div>
                <span className="text-gray-700" data-id="element-268">Indiferencia a tus éxitos</span>
              </li>
              <li className="flex items-start" data-id="element-269">
                <div className="bg-orange-100 text-orange-700 p-1 rounded-full mr-2 mt-0.5" data-id="element-270">
                  <span className="block h-1.5 w-1.5 rounded-full bg-current" data-id="element-271"></span>
                </div>
                <span className="text-gray-700" data-id="element-272">
                  Críticas frecuentes o menosprecio
                </span>
              </li>
            </ul>
          </div>
        </div>
        <div className="flex justify-center" data-id="element-273">
          <a href="/personal-growth" className="flex items-center bg-[#E91E63] text-white px-5 py-2 rounded-md hover:bg-[#D81B60]" data-id="element-274">
            Explora el Programa de Crecimiento Personal{' '}
            <ArrowRightIcon size={16} className="ml-2" data-id="element-275" />
          </a>
        </div>
      </div>
    </div>;
};
export default Consolidation;